import React, { useState } from 'react';
import { getDefaultAvatarWithInitial } from '../utils/defaultAvatar';
import './ProjectList.css';

function ProjectList({ projects, currentProject, friends, onSelectProject, onCreateProject, user, projectUnreadCounts = {} }) {
  const [showCreate, setShowCreate] = useState(false);
  const [projectName, setProjectName] = useState('');
  const [projectDesc, setProjectDesc] = useState('');
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  const handleCreate = () => {
    if (projectName.trim()) {
      onCreateProject(projectName.trim(), projectDesc.trim(), selectedMembers);
      setProjectName('');
      setProjectDesc('');
      setSelectedMembers([]);
      setShowCreate(false);
    }
  };

  const toggleMember = (userId) => {
    setSelectedMembers(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const filteredFriends = friends.filter(f =>
    !searchQuery || f.username?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    const diff = now - d;
    if (diff < 86400000) return 'Today';
    if (diff < 172800000) return 'Yesterday';
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  return (
    <div className="project-list">
      <div className="project-list-header">
        <button
          onClick={() => setShowCreate(!showCreate)}
          className="action-btn"
          title="New Project"
        >
          <span className="material-symbols-outlined">create_new_folder</span>
          <span className="action-label">New Project</span>
        </button>
      </div>

      {showCreate && (
        <div className="new-project-panel">
          <h4>Create a Project</h4>
          <input
            type="text"
            placeholder="Project name"
            value={projectName}
            onChange={(e) => setProjectName(e.target.value)}
            className="project-input"
            autoFocus
          />
          <input
            type="text"
            placeholder="Description (optional)"
            value={projectDesc}
            onChange={(e) => setProjectDesc(e.target.value)}
            className="project-input"
          />
          {friends.length > 0 && (
            <>
              <p className="invite-label">Invite friends (optional)</p>
              {friends.length > 4 && (
                <input
                  type="text"
                  placeholder="Search friends..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="project-input"
                />
              )}
              <div className="member-select-list">
                {filteredFriends.map(friend => (
                  <div
                    key={friend.userId}
                    className={`member-select-item ${selectedMembers.includes(friend.userId) ? 'selected' : ''}`}
                    onClick={() => toggleMember(friend.userId)}
                  >
                    <img
                      src={friend.photoURL || getDefaultAvatarWithInitial(friend.username)}
                      alt={friend.username}
                      className="member-select-avatar"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = getDefaultAvatarWithInitial(friend.username);
                      }}
                    />
                    <span className="member-select-name">{friend.username}</span>
                    {selectedMembers.includes(friend.userId) && (
                      <span className="material-symbols-outlined check-icon">check_circle</span>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
          <button
            onClick={handleCreate}
            disabled={!projectName.trim()}
            className="create-project-btn"
          >
            Create Project
          </button>
        </div>
      )}

      <div className="project-items">
        <h4>Projects</h4>
        {projects.length === 0 ? (
          <div className="empty-state">
            <span className="material-symbols-outlined empty-icon">folder_off</span>
            <p>No projects yet</p>
            <p className="hint">Create a project to organize your files</p>
          </div>
        ) : (
          projects.map(project => (
            <div
              key={project.projectId}
              className={`project-item ${currentProject?.projectId === project.projectId ? 'active' : ''}`}
              onClick={() => onSelectProject(project)}
            >
              <div className="project-item-icon">
                {project.photoURL ? (
                  <img
                    src={project.photoURL}
                    alt={project.name}
                    className="project-thumb"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.style.display = 'none';
                      e.currentTarget.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <span
                  className="project-icon-fallback material-symbols-outlined"
                  style={project.photoURL ? { display: 'none' } : undefined}
                >
                  folder_special
                </span>
              </div>
              <div className="project-item-details">
                <span className="project-item-name">{project.name}</span>
                <span className="project-item-meta">
                  {project.members?.length || 1} member{(project.members?.length || 1) !== 1 ? 's' : ''}
                  {project.updatedAt ? ` · ${formatDate(project.updatedAt)}` : ''}
                </span>
              </div>
              {projectUnreadCounts[project.projectId] > 0 && (
                <div className="project-unread-badge">
                  {projectUnreadCounts[project.projectId]}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default ProjectList;
